'use client';

import type { BadgeProps } from '../../components/badge/badge';
import type { IconName } from '../../components/icons/types';

import { cn } from '@polyseme/utils/cn/cn-utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Badge } from '../../components/badge/badge';
import { Icon } from '../../components/icons/icon';

const tabsListVariants = cva(
  'inline-flex items-center justify-center rounded-lg bg-muted text-muted-foreground',
  {
    variants: {
      size: {
        md: 'h-10 p-1',
        lg: 'h-12 p-1',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

type TabsSizeContextType = VariantProps<typeof tabsListVariants>['size'];

const TabsSizeContext = React.createContext<TabsSizeContextType>('md');

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
    size?: TabsSizeContextType;
  }
>(({ size = 'md', ...props }, ref) => (
  <TabsSizeContext.Provider value={size}>
    <TabsPrimitive.Root ref={ref} {...props} />
  </TabsSizeContext.Provider>
));
Tabs.displayName = TabsPrimitive.Root.displayName;

type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, ...props }, ref) => {
  const size = React.useContext(TabsSizeContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ size }), className)}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium text-foreground ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-sm',
  {
    variants: {
      size: {
        md: 'rounded-md px-3 py-1.5 text-sm',
        lg: 'rounded-md px-6 py-2 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export type TabsTriggerProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> & {
  icon?: IconName;
  badge?: BadgeProps;
};

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, children, icon, badge, ...props }, ref) => {
  const size = React.useContext(TabsSizeContext);

  const badgeProps =
    badge &&
    ({
      ...badge,
      size: badge.size ?? size ?? 'md',
      variant: badge.variant ?? 'default',
    } satisfies BadgeProps | undefined);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ size }), className)}
      {...props}
    >
      {!!icon && <Icon name={icon} size="sm" />}
      {children}
      {!!badgeProps && <Badge {...badgeProps} />}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
